/* @jsxImportSource react */

import type { DimensionFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { getIsAvailable } from '../../helpers/getIsAvailable/getIsAvailable';
import { TilesDimensionPrice } from '../TilesDimensionPrice/TilesDimensionPrice.client';
import type { TileButtonProps } from './TileButton/TileButton';
import { TileButton } from './TileButton/TileButton';
import { TileButtonDefault } from './TileButton/TileButtonDefault.client';
import type { VariationSelectionOnSelectHandler } from '../types';

export type TilesDimensionProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  data: DimensionFragmentFragment['values'];
} & VariationSelectionOnSelectHandler;

/**
 * TilesDimension to choose a product variation on Detailview
 * */
export const TilesDimension = ({ data, onSelect }: TilesDimensionProps) => (
  <div className="flex flex-wrap gap-1.5">
    {data.map((value, index) => {
      const { sku, displayText, availability, isSelected, price } = value;
      const isAvailable = getIsAvailable(availability);

      const key = sku || index;

      let mode: TileButtonProps['mode'] | null;
      switch (true) {
        case isSelected && isAvailable:
          mode = 'selected';
          break;
        case !isAvailable && !isSelected:
          mode = 'disabled';
          break;
        case !isAvailable && isSelected:
          mode = 'disabledAndSelected';
          break;
        default:
          mode = null;
      }

      if (mode) {
        return (
          <TileButton key={key} mode={mode}>
            <span className="py-1">
              {displayText}
              {price && (
                <>
                  <Divider className="-mx-2 my-1" />
                  <TilesDimensionPrice data={price} />
                </>
              )}
            </span>
          </TileButton>
        );
      }

      if (!sku) {
        return null;
      }
      return (
        <TileButtonDefault key={`${key}${sku}`} sku={sku} onSelect={onSelect}>
          {displayText}
          {price && (
            <>
              <Divider className="-mx-2 my-1" />
              <TilesDimensionPrice data={price} />
            </>
          )}
        </TileButtonDefault>
      );
    })}
  </div>
);
